import { useState } from "react";
import { connect } from "react-redux";
import {
  Modal, Row, Col, Button, ToggleButtonGroup, Table,
  ToggleButton, InputGroup, Form, FloatingLabel
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { cupsSchema } from "../../utils/validationSchemas";
import { autoconsumos } from "../../utils/constants/autoconsumos";
import { btn_search_sips_with_periods } from "../../services/cups";
import { CODIGOS_TARIFA_ATR } from "../../utils/constants/tarifas";

const SIPS_LABELS = {
  nombreEmpresaDistribuidora: "Distribuidora",
  cnae: "CNAE",
  consumoAnual: "Consumo anual",
  tipoPerfilConsumo: "Perfil de consumo",
  codigoPeajeEnVigor: "Tarifa de acceso (SIPS)",
  codigoTarifaATREnVigor: "Tarifa de acceso (SIPS)",
  codigoAutoconsumo: "Codigo de autoconsumo",
  tipoAutoconsumo: "Tipo de autoconsumo",
  potenciaMaximaBIE: "Potencia maxima BIE",
  fechaCaducidadBIE: "Caducidad BIE",
  valorDerechosExtension: "Valor derechos de extension",
  aplicacionBonoSocial: "Aplicacion bono social",
  potenciasContratadasEnWP1: "Potencia P1",
  potenciasContratadasEnWP2: "Potencia P2",
  potenciasContratadasEnWP3: "Potencia P3",
  potenciasContratadasEnWP4: "Potencia P4",
  potenciasContratadasEnWP5: "Potencia P5",
  potenciasContratadasEnWP6: "Potencia P6",
  caudalHorarioEnWh: "Caudal horario (Wh)",
  caudalMaximoDiarioEnWh: "Caudal maximo diario (Wh)",
  codigoPresion: "Codigo de presion",
  fechaUltimaInspeccion: "Fecha última inspección",
};

const getPotenciaNum = (rawKey) => {
  const normalized = String(rawKey || "");
  const match = normalized.match(/potencias.*wp(\d+)/i);
  return match ? parseInt(match[1], 10) : null;
};

const toHumanLabel = (rawKey) => {
  if (SIPS_LABELS[rawKey]) {
    return SIPS_LABELS[rawKey];
  }

  const normalized = String(rawKey || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  if (!normalized) {
    return "Campo SIPS";
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const ATR_KEYS = new Set(["codigotarifaatrenvigor", "codigopeojeenvigor", "tarifa_acceso_sips"]);

const formatSipsValue = (key, value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (String(key).toLowerCase() === "consumoanual" && !isNaN(value)) {
    return parseFloat(value).toFixed(2);
  }

  if (ATR_KEYS.has(String(key).toLowerCase())) {
    return CODIGOS_TARIFA_ATR[String(value)] || value;
  }

  return value;
};

const formatConsumption = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toFixed(2);
};

const periodToLabel = (row) => {
  const month = row?.month;
  const year = row?.year;
  if (month && year) return `${String(month).padStart(2, "0")}/${year}`;
  if (row?.periodo) return row.periodo;
  if (month) return `Mes ${String(month).padStart(2, "0")}`;
  return "Periodo";
};

const getPeriodNumber = (rowOrKey) => {
  const source = typeof rowOrKey === "string" ? rowOrKey : rowOrKey?.periodo;
  const match = String(source || "").toUpperCase().match(/P\s*(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

const getPeriodNumbers = (rows, anualPeriodos) => {
  const found = new Set();

  Object.keys(anualPeriodos || {}).forEach((key) => {
    const n = getPeriodNumber(key);
    if (n) found.add(n);
  });

  (rows || []).forEach((row) => {
    const n = getPeriodNumber(row);
    if (n) found.add(n);
  });

  return Array.from(found).sort((a, b) => a - b);
};

const normalizeAnualPeriodos = (anualPeriodos, rows) => {
  const normalized = {};

  // Prefer deriving annual by period from monthly rows so the summary stays
  // consistent with the table and CSV accumulation.
  (rows || []).forEach((row) => {
    const n = getPeriodNumber(row);
    const num = Number(row?.consumo);
    if (n && !Number.isNaN(num)) {
      normalized[n] = Math.round((normalized[n] || 0) * 100 + num * 100) / 100;
    }
  });

  if (Object.keys(normalized).length) {
    return normalized;
  }

  // Fallback: use backend annual-by-period values when there are no monthly rows.
  Object.entries(anualPeriodos || {}).forEach(([key, rawValue]) => {
    const n = getPeriodNumber(key);
    const num = Number(rawValue);
    if (n && !Number.isNaN(num)) {
      normalized[n] = Math.round((normalized[n] || 0) * 100 + num * 100) / 100;
    }
  });

  return normalized;
};

const sumConsumoRows = (rows) =>
  Math.round((rows || []).reduce((acc, row) => acc + (Number(row?.consumo) || 0), 0) * 100) / 100;

const buildMonthlyRowsByPeriod = (rows, periodNumbers) => {
  const grouped = new Map();

  (rows || []).forEach((row) => {
    const key = `${row?.year ?? ""}-${row?.month ?? ""}-${periodToLabel(row)}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        periodo: periodToLabel(row),
        year: row?.year ?? "",
        month: row?.month ?? "",
        values: {},
      });
    }

    const target = grouped.get(key);
    const n = getPeriodNumber(row);
    const consumo = Number(row?.consumo || 0);

    if (n && !Number.isNaN(consumo)) {
      target.values[n] = Math.round((target.values[n] || 0) * 100 + consumo * 100) / 100;
    }
  });

  const result = Array.from(grouped.values());
  result.forEach((item) => {
    item.total = Math.round(periodNumbers.reduce((sum, n) => sum + Number(item.values[n] || 0), 0) * 100) / 100;
  });

  result.sort((a, b) => {
    const yearA = Number(a.year || 9999);
    const yearB = Number(b.year || 9999);
    if (yearA !== yearB) return yearA - yearB;
    const monthA = Number(a.month || 99);
    const monthB = Number(b.month || 99);
    return monthA - monthB;
  });

  return result;
};

const buildCsv = (rows, total, anualPeriodos) => {
  const normalizedAnualPeriodos = normalizeAnualPeriodos(anualPeriodos, rows);
  const periodNumbers = getPeriodNumbers(rows, normalizedAnualPeriodos);
  const accumulatedTotal = sumConsumoRows(rows);
  const effectiveTotal = rows?.length ? accumulatedTotal : total;

  if (!periodNumbers.length) {
    const header = "Periodo;Año;Mes;Consumo_kWh";
    const body = rows.map((row) => {
      const periodo = periodToLabel(row);
      const year = row?.year ?? "";
      const month = row?.month ?? "";
      const consumo = row?.consumo ?? "";
      return `${periodo};${year};${month};${consumo}`;
    });
    const footer = `TOTAL;;;;${effectiveTotal ?? ""}`;
    return [header, ...body, footer].join("\n");
  }

  const monthly = buildMonthlyRowsByPeriod(rows, periodNumbers);
  const monthlyHeader = ["Periodo", "Año", "Mes", ...periodNumbers.map((n) => `P${n}_kWh`), "Total_mes_kWh"].join(";");
  const monthlyBody = monthly.map((row) => {
    const periodValues = periodNumbers.map((n) => (row.values[n] ?? "").toFixed ? Number(row.values[n] ?? 0).toFixed(2) : "");
    return [row.periodo, row.year, row.month, ...periodValues, Number(row.total ?? 0).toFixed(2)].join(";");
  });

  const annualHeader = ["Periodo_energia", "Consumo_anual_kWh"].join(";");
  const annualBody = periodNumbers.map((n) => [
    `P${n}`,
    Number(normalizedAnualPeriodos[n] ?? 0).toFixed(2),
  ].join(";"));

  const totalLine = ["TOTAL_ANUAL", Number(effectiveTotal ?? 0).toFixed(2)].join(";");

  return [
    monthlyHeader,
    ...monthlyBody,
    "",
    annualHeader,
    ...annualBody,
    totalLine,
  ].join("\n");
};

function GestionSips({ show, close }) {
  const [loading, setLoading] = useState(false);
  const [cups, setCups] = useState(false);
  const [consumoPeriodos, setConsumoPeriodos] = useState([]);
  const [consumoAnualTotalPeriodos, setConsumoAnualTotalPeriodos] = useState(null);
  const [consumoAnualPeriodos, setConsumoAnualPeriodos] = useState({});
  const [sector_type] = useState(false);

  const {
    register,
    control,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(cupsSchema(sector_type)),
  });

  const cupsName = watch("cups_name");
  const sectorType = watch("sector_type");
  const consumoAnualSips = cups?.consumoAnual;
  const anualPeriodosNormalizados = normalizeAnualPeriodos(consumoAnualPeriodos, consumoPeriodos);
  const periodNumbers = getPeriodNumbers(consumoPeriodos, anualPeriodosNormalizados);
  const monthlyRowsByPeriod = buildMonthlyRowsByPeriod(consumoPeriodos, periodNumbers);
  const totalAnualAcumulado = sumConsumoRows(consumoPeriodos);

  const cerrar = () => {
    close(false);
  };

  const onSearchCups = async () => {
    const data = getValues();
    setLoading(true);
    const result = await btn_search_sips_with_periods(data.cups_name, data.sector_type);
    setCups(result?.cups || false);
    setConsumoPeriodos(result?.consumoPeriodos || []);
    setConsumoAnualTotalPeriodos(result?.consumoAnualTotalPeriodos ?? null);
    setConsumoAnualPeriodos(result?.consumoAnualPeriodos || {});
    setLoading(false);
  };

  const exportConsumoPeriodos = () => {
    if (!consumoPeriodos?.length) return;
    const csvContent = buildCsv(consumoPeriodos, consumoAnualTotalPeriodos, consumoAnualPeriodos);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `sips_consumos_${sectorType || "sector"}_${cupsName || "cups"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      show={show}
      size="xl"
      backdrop="static"
      centered
      contentClassName="border-0 rounded-4 shadow-lg"
    >
      <Modal.Header className="bg-white border-bottom-0" closeButton onHide={cerrar}>
        <Modal.Title className="fw-bold text-primary">Gestión de SIPS</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="p-3">
          <Row className="justify-content-center align-items-center">
            <Col lg={10} md={12}>
              <div
                className="d-flex flex-column align-items-center justify-content-center p-4 mb-3 bg-light rounded-4 border border-light"
              >
                <label className="fw-bold text-muted text-uppercase mb-3 small">Selecciona Sector</label>
                <Controller
                  control={control}
                  name="sector_type"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <>
                      <ToggleButtonGroup
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        type="radio"
                        name="options"
                        className="shadow-sm rounded-pill"
                      >
                        <ToggleButton
                          id="electrico"
                          variant={sectorType === "electrico" ? "primary" : "outline-secondary"}
                          className="rounded-s-pill px-4 fw-bold"
                          value={"electrico"}
                          style={{ minWidth: '120px' }}
                        >
                          Electricidad
                        </ToggleButton>
                        <ToggleButton
                          id="gas"
                          variant={sectorType === "gas" ? "primary" : "outline-secondary"}
                          className="rounded-e-pill px-4 fw-bold"
                          value={"gas"}
                          style={{ minWidth: '120px' }}
                        >
                          Gas
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </>
                  )}
                />
              </div>

              <InputGroup className="shadow-sm rounded-4 overflow-hidden">
                <FloatingLabel controlId="cups_input" label="CUPS">
                  <Form.Control
                    {...register("cups_name")}
                    placeholder="CUPS"
                    className="border-0"
                    isInvalid={!!errors.cups_name?.message}
                  />
                </FloatingLabel>
                <Button
                  disabled={!cupsName || cupsName.length < 20}
                  className="px-4 fw-bold"
                  onClick={onSearchCups}
                  variant="primary"
                >
                  Buscar
                </Button>
              </InputGroup>
              {errors.cups_name?.message && (
                <div className="text-danger small mt-1 ms-2">
                  {errors.cups_name.message}
                </div>
              )}
            </Col>

            <Col xs={12}>
              {loading ? (
                <div className="d-flex flex-column justify-content-center align-items-center mt-5 mb-4 text-muted">
                  <div className="spinner-border text-primary mb-3" role="status"></div>
                  <h5 className="fw-bold">Buscando en Sips...</h5>
                </div>
              ) : cups ? (
                <div className="mt-4">
                  <div className="rounded-3 border border-light shadow-sm overflow-hidden mb-3">
                    <Table striped hover borderless size="sm" className="mb-0">
                      {
                        Object.entries(cups)
                          .sort(([keyA], [keyB]) => {
                            const pA = getPotenciaNum(keyA);
                            const pB = getPotenciaNum(keyB);

                            if (pA !== null && pB !== null) {
                              return pA - pB;
                            }
                            return keyA.localeCompare(keyB);
                          })
                          .map(([key, value]) => {
                            let displayValue = formatSipsValue(key, value);

                            if (key.toUpperCase() === 'CONSUMOANUAL' && !isNaN(value)) {
                              displayValue = parseFloat(value).toFixed(2);
                            }

                            if (key.toUpperCase() === 'CODIGOAUTOCONSUMO') {
                              const autoconsumo = autoconsumos.find(a => a.id === value);
                              if (autoconsumo) {
                                displayValue = `${value} - ${autoconsumo.name}`;
                              }
                            }

                            return (
                              <tbody key={key}>
                                <tr className="align-middle border-bottom">
                                  <td className="bg-light px-4 py-2 text-muted fw-bold small" style={{ width: '35%' }}>
                                    {toHumanLabel(key)}
                                  </td>
                                  <td className="px-4 py-2 text-secondary">{displayValue}</td>
                                </tr>
                              </tbody>
                            )
                          })
                      }
                    </Table>
                  </div>

                  <div className="rounded-3 border border-light shadow-sm overflow-hidden">
                    <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom">
                      <span className="fw-bold text-secondary">Desglose de consumo por mes y periodo de energia</span>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={exportConsumoPeriodos}
                        disabled={!consumoPeriodos?.length}
                      >
                        Exportar CSV
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <Table
                        striped
                        hover
                        borderless
                        size="sm"
                        className="mb-0"
                        style={{ minWidth: periodNumbers.length ? `${520 + periodNumbers.length * 110}px` : "620px" }}
                      >
                        <thead>
                          {periodNumbers.length ? (
                            <tr>
                              <th className="px-3 text-nowrap">Periodo</th>
                              <th className="px-3 text-nowrap">Año</th>
                              <th className="px-3 text-nowrap">Mes</th>
                              {periodNumbers.map((n) => (
                                <th key={`header-p-${n}`} className="px-3 text-end text-nowrap">P{n} (kWh)</th>
                              ))}
                              <th className="px-3 text-end text-nowrap" style={{ minWidth: '130px' }}>Total mes (kWh)</th>
                            </tr>
                          ) : (
                            <tr>
                              <th className="px-3 text-nowrap">Periodo</th>
                              <th className="px-3 text-nowrap">Año</th>
                              <th className="px-3 text-nowrap">Mes</th>
                              <th className="px-3 text-end text-nowrap">Consumo (kWh)</th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {consumoPeriodos?.length ? (
                            periodNumbers.length ? (
                              monthlyRowsByPeriod.map((row, idx) => (
                                <tr key={`${row?.periodo || "periodo"}-${idx}`}>
                                  <td className="px-3">{row.periodo}</td>
                                  <td className="px-3">{row.year || "-"}</td>
                                  <td className="px-3">{row.month || "-"}</td>
                                  {periodNumbers.map((n) => (
                                    <td key={`row-${idx}-p-${n}`} className="px-3 text-end">
                                      {formatConsumption(row.values[n])}
                                    </td>
                                  ))}
                                  <td className="px-3 text-end fw-bold">{formatConsumption(row.total)}</td>
                                </tr>
                              ))
                            ) : (
                              consumoPeriodos.map((row, idx) => (
                                <tr key={`${row?.periodo || "periodo"}-${idx}`}>
                                  <td className="px-3">{periodToLabel(row)}</td>
                                  <td className="px-3">{row?.year || "-"}</td>
                                  <td className="px-3">{row?.month || "-"}</td>
                                  <td className="px-3 text-end">{formatConsumption(row?.consumo)}</td>
                                </tr>
                              ))
                            )
                          ) : (
                            <tr>
                              <td colSpan={periodNumbers.length ? 5 + periodNumbers.length : 4} className="px-3 py-3 text-muted text-center">
                                {consumoAnualSips
                                  ? "SIPS devolvió consumo anual total, pero no desglose mensual/por periodos para este CUPS."
                                  : "No hay desglose de periodos disponible para este CUPS."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th className="px-3" colSpan={periodNumbers.length ? 3 + periodNumbers.length : 3}>Total anual acumulado</th>
                            <th className="px-3 text-end">{formatConsumption(totalAnualAcumulado)}</th>
                          </tr>
                          {consumoAnualTotalPeriodos !== null && consumoAnualTotalPeriodos !== undefined && (
                            <tr>
                              <th className="px-3 text-muted fw-normal" colSpan={periodNumbers.length ? 3 + periodNumbers.length : 3}>Total anual reportado por backend</th>
                              <th className="px-3 text-end text-muted fw-normal">{formatConsumption(consumoAnualTotalPeriodos)}</th>
                            </tr>
                          )}
                        </tfoot>
                      </Table>
                    </div>

                    {periodNumbers.length > 0 && (
                      <div className="border-top px-3 py-2 bg-white">
                        <div className="fw-bold text-secondary mb-2">Consumo anual por periodo de energia</div>
                        <div className="table-responsive">
                          <Table
                            borderless
                            size="sm"
                            className="mb-0"
                            style={{ minWidth: periodNumbers.length ? `${120 + periodNumbers.length * 90}px` : "260px" }}
                          >
                            <thead>
                              <tr>
                                {periodNumbers.map((n) => (
                                  <th key={`annual-header-p-${n}`} className="px-2">P{n}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                {periodNumbers.map((n) => (
                                  <td key={`annual-value-p-${n}`} className="px-2 fw-semibold">
                                    {formatConsumption(anualPeriodosNormalizados[n])}
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null
              }
            </Col>
          </Row>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default connect()(GestionSips);
